import { BodySecondary, H3, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { CompleteAccountModalContext, ModalContext, openReviewsModal } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { fetchSellersIfNeeded, getSeller } from '@/redux/modules/seller';
import { FormattedMessage, FormattedNumber, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getAuctioneerLogoUrl, getSellerUrl, imagePlaceholder, isItemPage } from '@/utils/urls';
import {
    getDisplayRating,
    getSellerRatingAllowPublicReviews,
    getSellerRatingTotalReviews,
} from '@/redux/modules/sellerRatings';
import { getLongAddressFormat } from '@liveauctioneers/caterwaul-components/lib/utils/address';
import { getSellerFollowerCountById } from '@/redux/modules/sellerFollowerCount';
import { isTopRatedHouse } from '@/redux/modules/topRatedSellers';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import { useTrackAskAQuestionClickedAnalytics } from '@/utils/amplitude/analytics';
import FollowSellerButton from '../FollowSellerButton/FollowSellerButton';
import isEqual from 'react-fast-compare';
import RatingDisplaySmall from '@/components/RatingDisplaySmall/RatingDisplaySmall';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import TopBadge from '@/components/Badges/TopBadge';

type Props = {
    className?: string;
    hideAskQuestionBtn?: boolean;
    hideFollowBtn?: boolean;
    hideFollowers?: boolean;
    hideImage?: boolean;
    itemId?: number;
    logoSize?: number;
    openOnBlank?: boolean;
    sellerId: number;
    showGetHelpModal?: () => void;
    showSendSellerMessageModal?: () => void;
};

const IMAGE_PADDING = 10;

const AuctioneerInfo = ({
    className = '',
    hideAskQuestionBtn = false,
    hideFollowBtn = false,
    hideFollowers = false,
    hideImage,
    itemId,
    logoSize = 140,
    openOnBlank = false,
    sellerId,
    showGetHelpModal,
    showSendSellerMessageModal,
}: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const { pathname } = useLocation();
    const trackAskAQuestionClickedAnalytics = useTrackAskAQuestionClickedAnalytics();

    const isOnItemPage = isItemPage(pathname);

    const { city, country, logoId, name, stateCode } = useAppSelector((state) => getSeller(state, sellerId));

    const location = getLongAddressFormat(city, country, stateCode);
    const sellerFollowerCount = useAppSelector((state) => getSellerFollowerCountById(state, sellerId));
    const sellerIsTopRated = useAppSelector((state) => isTopRatedHouse(state, sellerId));
    const allowPublicReviews = useAppSelector((state) => getSellerRatingAllowPublicReviews(state, sellerId));
    const totalReviews = useAppSelector((state) => getSellerRatingTotalReviews(state, sellerId));
    const imageSize = logoSize - IMAGE_PADDING * 2;

    useEffect(() => {
        // just in case we dont yet have the seller (Auction House) object, get it
        if (!logoId) {
            dispatch(fetchSellersIfNeeded([sellerId]));
        }
    }, [dispatch, logoId, sellerId]);

    const displayRating = useAppSelector((state) => getDisplayRating(state, sellerId));

    const sellerLogoImage = logoId ? (
        <img
            alt={name}
            className="max-h-[calc(100%-8px)] max-w-[calc(100%-8px)] object-contain"
            height={`${imageSize}`}
            loading="lazy"
            src={getAuctioneerLogoUrl(logoId, logoSize)}
            width={`${imageSize}`}
        />
    ) : (
        <img
            alt={name}
            className="max-h-[calc(100%-8px)] max-w-[calc(100%-8px)] object-contain"
            height={`${imageSize}`}
            src={imagePlaceholder}
            width={`${imageSize}`}
        />
    );

    const handleRatingClick = useCallback(() => {
        dispatch(openReviewsModal(sellerId));
    }, [dispatch, sellerId]);

    const displayFollowBtn = Boolean(sellerId) && !hideFollowBtn;
    const displayFollowers = Boolean(sellerFollowerCount >= 10) && !hideFollowers;

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.sendMessage,
            houseId: sellerId,
            id: itemId || sellerId,
            itemId,
            type: itemId
                ? CompleteAccountModalContext.AskQuestionItem
                : CompleteAccountModalContext.AskQuestionAuctioneer,
        }),
        [itemId, sellerId]
    );

    const onAskQuestionClicked = useCallback(() => {
        trackAskAQuestionClickedAnalytics();
        isOnItemPage ? showGetHelpModal() : showSendSellerMessageModal();
    }, [isOnItemPage, showGetHelpModal, showSendSellerMessageModal, trackAskAQuestionClickedAnalytics]);

    return (
        <div className={className}>
            <div className="flex flex-row items-center">
                {!hideImage && (
                    <div
                        className={cn(
                            'relative mr-md flex items-center justify-center border border-surface-200 bg-surface-50',
                            `w-[${logoSize - IMAGE_PADDING}px]`,
                            `h-[${logoSize - IMAGE_PADDING}px]`,
                            'sm:w-[100px] sm:h-[100px]',
                            'print:hidden'
                        )}
                    >
                        {sellerLogoImage}
                    </div>
                )}
                <div className="smMax:max-w-full [&_a]:no-underline">
                    <Link
                        className="flex flex-col hover:text-link-hover"
                        href={getSellerUrl(sellerId, name)}
                        target={openOnBlank ? '_blank' : '_self'}
                    >
                        <H3
                            className={cn('mr-xs flex items-center overflow-y-hidden leading-6 tracking-[0.5px]', {
                                'items-start': name?.length > 50,
                            })}
                            data-testid="itemPageSellerName"
                        >
                            {name}
                        </H3>
                    </Link>
                    {sellerIsTopRated && (
                        <TopBadge
                            className="whitespace-nowrap smMax:ml-0 smMax:mt-xs"
                            iconSize="small"
                        />
                    )}
                    <BodySecondary className="mb-0 block capitalize leading-6 text-text-secondary">
                        {location}
                    </BodySecondary>
                    {Boolean(allowPublicReviews) && (
                        <div className="mt-2xs leading-6">
                            <RatingDisplaySmall
                                onClick={handleRatingClick}
                                rating={displayRating}
                                reviewCount={totalReviews}
                                small
                                to={`${getSellerUrl(sellerId, name)}reviews`}
                            />
                        </div>
                    )}
                    {displayFollowers && (
                        <H5 className="inline-block">
                            <FormattedMessage
                                id="followers"
                                values={{
                                    count: (
                                        <FormattedNumber
                                            key="seller_follower_count"
                                            value={sellerFollowerCount}
                                        />
                                    ),
                                }}
                            />
                        </H5>
                    )}
                </div>
            </div>
            <div
                className={cn({
                    'mt-md flex flex-1 flex-row flex-wrap no-underline print:hidden':
                        !hideAskQuestionBtn || displayFollowBtn,
                })}
            >
                {!hideAskQuestionBtn && (
                    <ShouldLogInContainer
                        modalContext={modalContext}
                        requireCompleteProfile
                        requireLogin
                        requireVerifiedAddress
                        runFunction={onAskQuestionClicked}
                    >
                        <Button
                            className="mb-xs smMax:mb-sm"
                            data-testid="itemPageAskSellerQuestionButton"
                            size="sm"
                            variant="outlined"
                        >
                            {formatMessage({
                                id: 'bidding_console.console.currentLiveItem.auctioneerInfo.buttons.askAQuestion',
                            })}
                        </Button>
                    </ShouldLogInContainer>
                )}
                {displayFollowBtn && (
                    <FollowSellerButton
                        className="mb-xs ml-md w-auto smMax:mb-sm smMax:max-w-full"
                        sellerId={sellerId}
                        small
                    />
                )}
            </div>
        </div>
    );
};

export default memo<Props>(AuctioneerInfo, isEqual);
